import { IconProps } from '@euler/app/components/icons/types';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Option } from '@euler/model';
import { CarwashType } from '@euler/model/enum';
import { memo } from 'react';
import { Path, Svg } from 'react-native-svg';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { IconWrapper } from '../components/IconWrapper';
import InlineRadios from '../components/InlineRadios';
import { Row } from '../components/Row';
import { RowErrorText } from '../components/RowErrorText';

const kOptions: Option<CarwashType>[] = [
  { value: CarwashType.NoWash, text: '不洗车' },
  {
    value: CarwashType.FreeWash,
    text: '免费',
  },
  { value: CarwashType.PaidWash, text: '收费' },
];

export const CarwashTypeRow = memo(
  ({ required, ...props }: { required?: boolean; disabled?: boolean }) => {
    const {
      theme,
      formController: { form },
    } = useOrderFlowContext();

    const { iconColor } = theme;

    return (
      <Row
        errorProp="carwashType"
        note={<RowErrorText error={form.carwashType.error} />}
        title="是否洗车"
        required={required}
        icon={
          <IconWrapper>
            <RowIcon color={iconColor} />
          </IconWrapper>
        }
        detail={
          <InlineRadios
            options={kOptions}
            value={form.carwashType.value}
            disabled={props.disabled}
            onChange={form.carwashType.update}
          />
        }
        {...props}
      />
    );
  },
);

const RowIcon = memo(({ width, height, color, style, ...props }: IconProps) => {
  const { rpx } = useResponsiveContext();
  return (
    <Svg
      viewBox="0 0 21 21.949"
      width={width ?? rpx(19)}
      height={height ?? rpx(20)}
      style={style}
      fill="none"
      {...props}
    >
      <Path
        d="M6.6814 2.44212C6.6814 1.25992 5.71057 0 5.12695 0C4.53113 0 3.57251 1.39082 3.57251 2.44212C3.57251 3.30115 4.26794 3.99656 5.12695 3.99656C5.98596 3.99656 6.6814 3.30115 6.6814 2.44212Z"
        fillRule="evenodd"
        fill={color}
      />
      <Path
        d="M11.8928 2.44212C11.8928 1.25992 10.922 0 10.3384 0C9.74255 0 8.78394 1.39082 8.78394 2.44212C8.78394 3.30115 9.47937 3.99656 10.3384 3.99656C11.1974 3.99656 11.8928 3.30115 11.8928 2.44212Z"
        fillRule="evenodd"
        fill={color}
      />
      <Path
        d="M17.0948 2.44212C17.0948 1.25992 16.124 0 15.5404 0C14.9446 0 13.986 1.39082 13.986 2.44212C13.986 3.30115 14.6814 3.99656 15.5404 3.99656C16.3994 3.99656 17.0948 3.30115 17.0948 2.44212Z"
        fillRule="evenodd"
        fill={color}
      />
      <Path
        d="M21 11.2997C21 10.2716 20.4818 9.43713 19.8438 9.43713C19.541 9.43713 19.2656 9.62666 19.0597 9.9321L17.1493 5.50465C17.0844 5.35432 16.9768 5.22629 16.8398 5.13639C16.703 5.04649 16.5427 4.99865 16.3789 4.99878L4.43018 4.99878C4.08521 4.99878 3.77563 5.20876 3.65027 5.53056L1.92395 9.90892C1.71948 9.61575 1.45081 9.43713 1.15625 9.43713C0.516846 9.43713 0 10.2716 0 11.2997C0 12.3156 0.505859 13.1405 1.13452 13.161L1.13452 21.1105C1.13452 21.5741 1.5094 21.949 1.97302 21.949L3.59973 21.949C4.06335 21.949 4.43835 21.5741 4.43835 21.1105L4.43835 20.6741C4.43835 20.2105 4.81335 19.8355 5.27698 19.8355L15.7694 19.8355C16.233 19.8355 16.608 20.2105 16.608 20.6741L16.608 21.1105C16.608 21.5741 16.983 21.949 17.4465 21.949L19.0734 21.949C19.5369 21.949 19.9119 21.5741 19.9119 21.1105L19.9119 13.1569C20.5187 13.0969 21 12.2897 21 11.2997ZM3.41296 11.2152L4.72742 6.85729C4.76831 6.72231 4.89246 6.63094 5.03284 6.63094L16.0149 6.63094C16.1553 6.63094 16.2794 6.72231 16.3203 6.85729L17.6361 11.2152C17.6975 11.4197 17.5448 11.6256 17.3307 11.6256L3.71704 11.6256C3.50427 11.6256 3.35022 11.4197 3.41296 11.2152ZM4.85291 16.6871C3.92432 16.6871 3.17163 15.9344 3.17163 15.0059C3.17163 14.0773 3.92432 13.3246 4.85291 13.3246C5.78149 13.3246 6.53418 14.0773 6.53418 15.0059C6.53418 15.9344 5.78149 16.6871 4.85291 16.6871ZM16.1744 16.6871C15.2458 16.6871 14.4932 15.9344 14.4932 15.0059C14.4932 14.0773 15.2458 13.3246 16.1744 13.3246C17.103 13.3246 17.8557 14.0773 17.8557 15.0059C17.8557 15.9344 17.103 16.6871 16.1744 16.6871Z"
        fillRule="evenodd"
        fill={color}
      />
      <Path
        d="M19.8424 9.43713C19.5397 9.43713 19.2642 9.62666 19.0583 9.9321L17.1479 5.50465C17.083 5.35432 16.9755 5.22629 16.8385 5.13639C16.7015 5.04649 16.5414 4.99865 16.3776 4.99878L4.43018 4.99878C4.08521 4.99878 3.77563 5.20876 3.65027 5.53056L1.92395 9.90892C1.71948 9.61575 1.45081 9.43713 1.15625 9.43713C0.516846 9.43713 0 10.2716 0 11.2997C0 12.3156 0.505859 13.1405 1.13452 13.161L1.13452 21.1105C1.13452 21.2168 1.15625 21.3177 1.19312 21.4118C2.04944 21.6504 2.93567 21.8181 3.84387 21.9081C4.1875 21.8031 4.4397 21.4882 4.4397 21.1091L4.4397 20.6728C4.4397 20.2092 4.8147 19.8342 5.27832 19.8342L13.3219 19.8342C16.9489 17.6989 19.6392 14.1427 20.6263 9.92937C20.4177 9.62393 20.1437 9.43713 19.8424 9.43713ZM3.41296 11.2152L4.72742 6.85729C4.76831 6.72231 4.89246 6.63094 5.03284 6.63094L16.0149 6.63094C16.1553 6.63094 16.2794 6.72231 16.3203 6.85729L17.6361 11.2152C17.6975 11.4197 17.5448 11.6256 17.3307 11.6256L3.71704 11.6256C3.50427 11.6256 3.35022 11.4197 3.41296 11.2152ZM4.85291 16.6871C3.92432 16.6871 3.17163 15.9344 3.17163 15.0059C3.17163 14.0773 3.92432 13.3246 4.85291 13.3246C5.78149 13.3246 6.53418 14.0773 6.53418 15.0059C6.53418 15.9344 5.78149 16.6871 4.85291 16.6871ZM16.1744 16.6871C15.2458 16.6871 14.4932 15.9344 14.4932 15.0059C14.4932 14.0773 15.2458 13.3246 16.1744 13.3246C17.103 13.3246 17.8557 14.0773 17.8557 15.0059C17.8557 15.9344 17.103 16.6871 16.1744 16.6871Z"
        fillRule="evenodd"
        fill={color}
      />
      <Path
        d="M15.5404 0C14.9445 0 13.986 1.39082 13.986 2.44212C13.986 3.30115 14.6813 3.99656 15.5404 3.99656C15.9004 3.99656 16.2317 3.87247 16.4962 3.66658C16.6476 2.84708 16.7362 2.00578 16.7552 1.14674C16.4198 0.490875 15.9044 0 15.5404 0ZM1.13306 16.4335C2.00574 16.4335 2.86072 16.3598 3.69385 16.2221C3.52869 16.0655 3.39722 15.8769 3.3075 15.6678C3.21765 15.4587 3.17139 15.2334 3.17163 15.0058C3.17163 14.0773 3.92432 13.3246 4.85291 13.3246C5.78149 13.3246 6.53418 14.0773 6.53418 15.0058C6.53418 15.1776 6.50818 15.3426 6.46045 15.4995C8.73352 14.6745 10.7611 13.3369 12.4042 11.6256L3.71838 11.6256C3.50427 11.6256 3.35156 11.4197 3.41296 11.2152L4.72742 6.85728C4.76831 6.72229 4.89246 6.63094 5.03284 6.63094L15.6344 6.63094C15.8472 6.10051 16.0326 5.55646 16.188 4.99876L4.43018 4.99876C4.08521 4.99876 3.77563 5.20876 3.65027 5.53055L1.92395 9.90891C1.71948 9.61574 1.45081 9.43712 1.15625 9.43712C0.516846 9.43712 0 10.2716 0 11.2997C0 12.3156 0.505859 13.1405 1.13452 13.161L1.13452 16.4335L1.13306 16.4335Z"
        fillRule="evenodd"
        fill={color}
      />
      <Path
        d="M10.3385 0C9.74255 0 8.78406 1.39082 8.78406 2.44212C8.78406 3.24388 9.39087 3.90384 10.1708 3.98701C10.7419 3.1391 11.2283 2.23709 11.6229 1.29401C11.2916 0.565872 10.7284 0 10.3385 0ZM3.97339 9.35803L4.72742 6.85728C4.76843 6.72229 4.89246 6.63094 5.03296 6.63094L7.92908 6.63094C8.46631 6.1237 8.9668 5.57828 9.42761 4.99876L4.43018 4.99876C4.08521 4.99876 3.77576 5.20876 3.65027 5.53055L1.92407 9.90891C1.71948 9.61574 1.45093 9.43712 1.15637 9.43712C0.66272 9.43712 0.242798 9.93481 0.076416 10.6343C1.44678 10.378 2.75439 9.94572 3.97339 9.35803Z"
        fillRule="evenodd"
        fill={color}
      />
    </Svg>
  );
});
